/**
 * Business impact widget for the personal dashboard.
 * Shows aggregated metrics
 */

import { ArrowUp, ArrowDown, Info } from '@phosphor-icons/react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { DashboardInsight } from '../types'

interface ImpactSummaryWidgetProps {
  insights: DashboardInsight[]
}

export function ImpactSummaryWidget({ insights }: ImpactSummaryWidgetProps) {
  const uniqueProducts = new Set(insights.flatMap((i) => i.affectedProducts))
  const uniqueNodes = new Set(insights.map((i) => i.technologyNode))
  const totalLots = insights.reduce((sum, i) => sum + i.lotsImpacted, 0)
  const totalWafers = insights.reduce((sum, i) => sum + i.wafersImpacted, 0)

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-[#e6e6e6] bg-white">
      <div className="flex items-center gap-2 px-5 pt-5">
        <h3 className="text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]">Business Impact</h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="flex items-center">
                <Info className="h-5 w-5 text-[#727272]" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs text-sm">
                Aggregated metrics showing the total number of products, lots, wafers, and technology nodes affected by insights across all workflows in the dashboard scope.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-3 p-5">
        <div className="flex items-center justify-between rounded-xl border border-[#e6e6e6] bg-[#f5f5f5] px-5 py-2.5">
          <div className="flex items-center gap-3">
            <p className="text-[32px] font-semibold leading-none text-[#1d1d1d]">{uniqueProducts.size}</p>
            <div className="flex items-center gap-1">
              <ArrowUp className="h-3 w-3 text-[#12a84a]" />
              <p className="text-base font-medium text-[#12a84a]">4.5%</p>
            </div>
          </div>
          <p className="text-base tracking-[0.16px] text-[#535353]">Products</p>
        </div>

        <div className="flex items-center justify-between rounded-xl border border-[#e6e6e6] bg-[#f5f5f5] px-5 py-2.5">
          <div className="flex items-center gap-3">
            <p className="text-[32px] font-semibold leading-none text-[#1d1d1d]">{totalLots}</p>
            <div className="flex items-center gap-1">
              <ArrowDown className="h-3 w-3 text-[#d73340]" />
              <p className="text-base font-medium text-[#d73340]">2.1%</p>
            </div>
          </div>
          <p className="text-base tracking-[0.16px] text-[#535353]">Lots</p>
        </div>

        <div className="flex items-center justify-between rounded-xl border border-[#e6e6e6] bg-[#f5f5f5] px-5 py-2.5">
          <div className="flex items-center gap-3">
            <p className="text-[32px] font-semibold leading-none text-[#1d1d1d]">{totalWafers}</p>
            <div className="flex items-center gap-1">
              <ArrowDown className="h-3 w-3 text-[#d73340]" />
              <p className="text-base font-medium text-[#d73340]">1.8%</p>
            </div>
          </div>
          <p className="text-base tracking-[0.16px] text-[#535353]">Wafers</p>
        </div>

        <div className="flex items-center justify-between rounded-xl border border-[#e6e6e6] bg-[#f5f5f5] px-5 py-2.5">
          <div className="flex items-center gap-3">
            <p className="text-[32px] font-semibold leading-none text-[#1d1d1d]">{uniqueNodes.size}</p>
            <div className="flex items-center gap-1">
              <ArrowUp className="h-3 w-3 text-[#12a84a]" />
              <p className="text-base font-medium text-[#12a84a]">0.5%</p>
            </div>
          </div>
          <p className="text-base tracking-[0.16px] text-[#535353]">Nodes</p>
        </div>
      </div>

      <div className="border-t border-[#e6e6e6] px-5 py-3 text-xs tracking-[0.12px] text-[#727272]">
        Aggregated across all workflows in scope
      </div>
    </div>
  )
}
